import { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import GridLayout from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import toast from 'react-hot-toast'
import { Settings, Plus, Save, Share2, User, LogOut, LayoutGrid, X } from 'lucide-react'
import { dashboardAPI, tabAPI, widgetAPI } from '../services/api'
import { useDashboardStore } from '../stores/dashboardStore'
import { useAuthStore } from '../stores/authStore'
import { useTheme } from '../contexts/ThemeContext'
import { useModalClose } from '../hooks/useModalClose'
import DashboardTabs from '../components/DashboardTabs'
import Widget from '../components/Widget'
import AddWidgetModal from '../components/AddWidgetModal'
import ShareDashboardModal from '../components/ShareDashboardModal'
import ThemeSelector from '../components/ThemeSelector'
import VoiceCommand from '../components/VoiceCommand'
import DashboardFooter from '../components/DashboardFooter'

export default function Dashboard() {
  const { dashboardId } = useParams()
  const { user, logout } = useAuthStore()
  const { currentTheme, loading: themeLoading } = useTheme()
  const logoSrc = currentTheme?.is_dark ? '/DashitLogo-white.png' : '/DashitLogo.png'
  const {
    currentDashboardId,
    currentTabId,
    tabs,
    widgets,
    isEditMode,
    expandedWidgetId,
    setCurrentDashboard,
    setCurrentTab,
    setTabs,
    setWidgets,
    setEditMode,
    setExpandedWidget,
  } = useDashboardStore()
  
  // Modal close handler for expanded widget
  useModalClose(() => setExpandedWidget(null))

  const [layout, setLayout] = useState<any[]>([])
  const [showAddWidget, setShowAddWidget] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [gridWidth, setGridWidth] = useState(window.innerWidth - 64) // Full width minus padding (32px container + 32px grid)
  const containerRef = useRef<HTMLDivElement>(null)

  // Fetch dashboards
  const { data: dashboardsData, isLoading: dashboardsLoading } = useQuery({
    queryKey: ['dashboards'],
    queryFn: async () => {
      const { data } = await dashboardAPI.getAll()
      return data
    },
  })

  // Fetch tabs for current dashboard
  const { data: tabsData, refetch: refetchTabs } = useQuery({
    queryKey: ['tabs', currentDashboardId],
    queryFn: async () => {
      if (!currentDashboardId) return []
      const { data } = await tabAPI.getByDashboard(currentDashboardId)
      return data
    },
    enabled: !!currentDashboardId,
  })

  // Fetch widgets for current tab
  const { data: widgetsData, refetch: refetchWidgets } = useQuery({
    queryKey: ['widgets', currentTabId],
    queryFn: async () => {
      if (!currentTabId) return []
      const { data } = await widgetAPI.getByTab(currentTabId)
      return data
    },
    enabled: !!currentTabId,
  })

  // Initialize dashboard
  useEffect(() => {
    if (dashboardsData && dashboardsData.own && dashboardsData.own.length > 0) {
      const targetDashboard = dashboardId
        ? dashboardsData.own.find((d: any) => d.id === parseInt(dashboardId))
        : dashboardsData.own[0]

      if (targetDashboard) {
        setCurrentDashboard(targetDashboard.id)
      }
    }
  }, [dashboardsData, dashboardId])

  // Update tabs when data changes
  useEffect(() => {
    if (tabsData) {
      setTabs(tabsData)
      if (tabsData.length > 0 && !currentTabId) {
        setCurrentTab(tabsData[0].id)
      }
    }
  }, [tabsData])

  // Update widgets when data changes
  useEffect(() => {
    if (widgetsData && Array.isArray(widgetsData)) {
      setWidgets(widgetsData)
      
      // Update layout for grid
      const newLayout = widgetsData.map((widget: any) => {
        // Search widgets can be smaller
        const isSearchWidget = widget.app_slug === 'google-search' || widget.app_slug === 'chatgpt-search'
        
        return {
          i: widget.id.toString(),
          x: widget.position_x,
          y: widget.position_y,
          w: widget.width,
          h: widget.height,
          minW: 2,
          minH: isSearchWidget ? 1 : 2,
        }
      })
      setLayout(newLayout)
    }
  }, [widgetsData])

  // Measure container width for responsive grid
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        // Subtract horizontal padding (px-4 = 16px * 2 = 32px)
        setGridWidth(containerRef.current.offsetWidth - 32)
      }
    }

    // Update width immediately
    updateWidth()
    
    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      updateWidth()
    })
    
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [widgets, currentTabId]) // Recalculate when widgets or tab changes

  const handleLayoutChange = (newLayout: any[]) => {
    if (!isEditMode) return
    setLayout(newLayout)
  }

  const handleSaveLayout = async () => {
    try {
      const widgetUpdates = layout.map((item) => ({
        id: parseInt(item.i),
        x: item.x,
        y: item.y,
        width: item.w,
        height: item.h,
      }))

      await widgetAPI.reorder(widgetUpdates)
      toast.success('Layout saved!')
      setEditMode(false)
    } catch (error) {
      toast.error('Failed to save layout')
    }
  }

  const currentTab = Array.isArray(tabs) ? tabs.find((t) => t.id === currentTabId) : undefined

  // Show loading state while data is being fetched
  if (dashboardsLoading || themeLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  // Show message if user has no dashboards
  if (dashboardsData && dashboardsData.own && dashboardsData.own.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8">
          <LayoutGrid size={64} className="text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome to Dashit.io!</h2>
          <p className="text-gray-600 mb-6">You don't have any dashboards yet. Let's create one to get started.</p>
          <button
            onClick={async () => {
              try {
                await dashboardAPI.create({ name: 'My Dashboard', description: 'My personal dashboard' })
                window.location.reload()
              } catch (error) {
                toast.error('Failed to create dashboard')
              }
            }}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Create Your First Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--color-background)', color: 'var(--color-text-primary)' }}>
      <div className="w-full px-3 py-2 border-b" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <img
                src={logoSrc}
                alt="Dashit"
                className="h-6 w-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling.style.display = 'block';
                }}
              />
              <span className="text-base font-bold text-primary-600 whitespace-nowrap hidden">Dashit</span>
            </Link>
            <DashboardTabs />
          </div>

          <div className="flex items-center gap-2">
            {isEditMode ? (
              <>
                <button
                  onClick={handleSaveLayout}
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition"
                >
                  <Save size={14} />
                  Save
                </button>
                <button
                  onClick={() => setEditMode(false)}
                  className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowShareModal(true)}
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition"
                >
                  <Share2 size={14} />
                  Share
                </button>
                <button
                  onClick={() => setShowAddWidget(true)}
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 transition"
                >
                  <Plus size={14} />
                  Add Widget
                </button>
                <button
                  onClick={() => setEditMode(true)}
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
                >
                  <Settings size={14} />
                  Edit
                </button>
              </>
            )}
            
            <div className="h-5 w-px bg-gray-300 mx-1"></div>
            
            <ThemeSelector />
            
            <Link
              to="/profile"
              className="flex items-center gap-1.5 text-gray-700 text-sm hover:bg-gray-100 px-2 py-1 rounded transition"
              title="Go to Profile"
            >
              <User size={14} />
              <span>{user?.username}</span>
            </Link>
            
            <button
              onClick={logout}
              className="flex items-center gap-1 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded transition"
            >
              <LogOut size={14} />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="w-full">
        <div
          ref={containerRef}
          className="relative px-4 py-3 w-full"
          style={{
            minHeight: 'calc(100vh - 70px)',
            overflow: 'visible',
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <GridLayout
            className="layout"
            layout={layout}
            cols={12}
            rowHeight={60}
            width={gridWidth}
            onLayoutChange={handleLayoutChange}
            isDraggable={isEditMode}
            isResizable={isEditMode}
            compactType={isEditMode ? 'vertical' : null}
            preventCollision={!isEditMode}
          >
            {widgets.map((widget) => (
              <div key={widget.id}>
                <Widget 
                  widget={widget} 
                  isEditMode={isEditMode}
                  isExpanded={false}
                />
              </div>
            ))}
          </GridLayout>
        </div>
      </div>

      {/* Expanded Widget Modal */}
      {expandedWidgetId && (() => {
        const expandedWidget = widgets.find(w => w.id === expandedWidgetId)
        return expandedWidget && createPortal(
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-[9998] flex items-center justify-center p-4"
            onClick={() => setExpandedWidget(null)}
          >
            <div 
              className="rounded-xl shadow-2xl w-[90vw] h-[90vh] overflow-hidden"
              style={{ backgroundColor: 'var(--color-widget-bg)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Widget Content - No wrapper, widget manages its own layout */}
              <Widget 
                widget={expandedWidget} 
                isEditMode={false}
                isExpanded={true}
              />
            </div>
          </div>,
          document.body
        )
      })()}

      {showAddWidget && (
        <AddWidgetModal
          onClose={() => setShowAddWidget(false)}
          onAdd={async () => {
            await refetchWidgets()
            setShowAddWidget(false)
          }}
        />
      )}

      {showShareModal && currentDashboardId && (
        <ShareDashboardModal
          dashboardId={currentDashboardId}
          dashboardName={dashboardsData?.own?.find((d: any) => d.id === currentDashboardId)?.name || 'Dashboard'}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* Voice Command Button */}
      <VoiceCommand />

      {/* Footer */}
      <DashboardFooter />
    </div>
  )
}

